'use client';

import type { VariantProps } from 'class-variance-authority';
import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cn } from '@savvy-kit/lib/utils';
import { cva } from 'class-variance-authority';

const textVariants = cva('', {
  variants: {
    variant: {
      title: 'typography-title',
      h1: 'typography-h1',
      h2: 'typography-h2',
      h3: 'typography-h3',
      h4: 'typography-h4',
      body: 'typography-body',
      'body-sm': 'typography-body-sm',
      'subtitle-lg': 'typography-subtitle-lg',
      'subtitle-sm': 'typography-subtitle-sm',
      caption: 'typography-caption',
      overline: 'typography-overline',
    },
    color: {
      default: 'text-foreground',
      dark: 'text-dark',
      secondary: 'text-secondary',
      primary: 'text-primary',
      success: 'text-success',
      disabled: 'text-disabled',
      light: 'text-light',
      destructive: 'text-destructive',
      warning: 'text-warning',
      info: 'text-info',
      inherit: 'text-inherit',
    },
  },
  defaultVariants: {
    variant: 'body',
    color: 'default',
  },
  compoundVariants: [
    { variant: 'body-sm', color: 'default', className: 'text-secondary' },
    { variant: 'caption', color: 'default', className: 'text-secondary' },
  ],
});

type TextElement = 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span';

const DEFAULT_ELEMENT_MAP: Record<string, TextElement> = {
  title: 'h1',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  body: 'p',
  'body-sm': 'p',
  'subtitle-lg': 'p',
  'subtitle-sm': 'p',
  caption: 'span',
  overline: 'span',
};

type TextProps = Omit<useRender.ComponentProps<'p'>, 'color'> & VariantProps<typeof textVariants>;

function Text({ className, variant = 'body', color, render, ...props }: TextProps) {
  const defaultTagName = DEFAULT_ELEMENT_MAP[variant ?? 'body'] ?? 'p';
  return useRender({
    defaultTagName,
    props: mergeProps<'p'>(
      {
        className: cn(textVariants({ variant, color }), className),
      },
      props,
    ),
    render,
    state: { slot: 'text' },
  });
}

export { Text, textVariants };
export type { TextProps };
